import React from 'react';
import { Text, View } from '@react-pdf/renderer';
import { convertHTMLToPDFWithFallback } from './htmlToPdfConverter';
import { createT } from './simpleTranslate';

/**
 * Content Renderer for PDF Generation
 * 
 * Wrapper component that handles different content types and rendering strategies.
 * Provides a unified interface for rendering various content formats in PDFs.
 */

export type ContentType = 'html' | 'plaintext' | 'markdown';
export type FallbackStrategy = 'plaintext' | 'empty' | 'error';

interface PDFContentRendererProps {
  content: string;
  contentType?: ContentType;
  fallbackStrategy?: FallbackStrategy;
  maxLength?: number;
  className?: string;
}

/**
 * Main content renderer component for PDFs
 */
export const PDFContentRenderer: React.FC<PDFContentRendererProps> = ({
  content,
  contentType = 'html',
  fallbackStrategy = 'plaintext',
  maxLength,
  className,
}) => {
  const t = createT('fr'); // Default to French, could be passed as prop
  
  // Handle empty or invalid content
  if (!content || typeof content !== 'string') {
    return fallbackStrategy === 'empty' ? null : (
      <Text style={{ fontSize: 10, color: '#666' }}>
        {fallbackStrategy === 'error' ? t('general.error_invalid_content', 'Erreur: Contenu invalide') : t('general.no_content', 'Aucun contenu')}
      </Text>
    );
  }

  // Truncate content if maxLength is specified
  const processedContent = maxLength && content.length > maxLength 
    ? content.substring(0, maxLength) + '...'
    : content;

  try {
    switch (contentType) {
      case 'html':
        return (
          <View>
            {convertHTMLToPDFWithFallback(processedContent, fallbackStrategy)}
          </View>
        );
      
      case 'markdown':
        // Basic markdown support (can be extended)
        return (
          <View>
            {renderBasicMarkdown(processedContent)}
          </View>
        );
      
      case 'plaintext':
      default:
        return (
          <Text style={{ fontSize: 10, fontFamily: 'Montserrat' }}>
            {processedContent}
          </Text>
        );
    }
  } catch (error) {
    console.error('Content rendering error:', error);
    
    if (fallbackStrategy === 'error') {
      return (
        <Text style={{ fontSize: 10, color: '#ff0000' }}>
          {t('general.content_render_error', 'Erreur lors du rendu du contenu')}
        </Text>
      );
    }
    
    if (fallbackStrategy === 'empty') {
      return null;
    }
    
    // Fallback to plain text
    const plainText = content.replace(/<[^>]*>/g, '').trim();
    return (
      <Text style={{ fontSize: 10, fontFamily: 'Montserrat', color: '#666' }}>
        {plainText}
      </Text>
    );
  }
};

/**
 * Specialized renderer for clinical notes
 * Optimized for the HTML content found in request.typeSpecificData.notes
 */
export const PDFClinicalNotesRenderer: React.FC<{
  notes: string;
  fallbackStrategy?: FallbackStrategy;
}> = ({ notes, fallbackStrategy = 'plaintext' }) => {
  
  // Handle null/undefined notes
  if (!notes || typeof notes !== 'string') {
    return null;
  }
  
  // Enhanced HTML to PDF converter with table support
  const renderHTML = (htmlContent: string) => {
    const elements: React.ReactNode[] = [];
    
    // First, extract and render any tables
    const tableMatches = htmlContent.match(/<table[^>]*>[\s\S]*?<\/table>/gi) || [];
    let contentWithoutTables = htmlContent;
    
    // Replace tables with placeholders and collect them
    const tablePlaceholders: { [key: string]: string } = {};
    tableMatches.forEach((tableHTML, tableIndex) => {
      const placeholder = `__TABLE_PLACEHOLDER_${tableIndex}__`;
      tablePlaceholders[placeholder] = tableHTML;
      contentWithoutTables = contentWithoutTables.replace(tableHTML, placeholder);
    });
    
    // Split by common HTML tags (excluding tables which we handle separately)
    // Added support for <div>, <textarea>, and <span> tags
    const parts = contentWithoutTables.split(/(<h[1-6][^>]*>.*?<\/h[1-6]>|<p[^>]*>.*?<\/p>|<div[^>]*>.*?<\/div>|<strong[^>]*>.*?<\/strong>|<em[^>]*>.*?<\/em>|<ul[^>]*>.*?<\/ul>|<ol[^>]*>.*?<\/ol>|<li[^>]*>.*?<\/li>|<textarea[^>]*>.*?<\/textarea>|<span[^>]*>.*?<\/span>|__TABLE_PLACEHOLDER_\d+__)/gi);
    
    parts.forEach((part, index) => {
      if (!part.trim()) return;
      
      // Check if this is a table placeholder
      if (part.match(/__TABLE_PLACEHOLDER_\d+__/)) {
        const tableHTML = tablePlaceholders[part];
        if (tableHTML) {
          elements.push(renderTable(tableHTML, index));
        }
        return;
      }
      
      if (part.match(/<h[1-6]/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 12, fontWeight: 'bold', marginTop: 10, marginBottom: 6, color: '#333' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<p/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, marginBottom: 6, lineHeight: 1.4, color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<div/i)) {
        // Handle div tags - extract text content and render
        const text = part.replace(/<[^>]*>/g, '').replace(/\n/g, ' ').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, marginBottom: 4, lineHeight: 1.4, color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<textarea/i)) {
        // Handle textarea tags - extract text content
        const text = part.replace(/<[^>]*>/g, '').replace(/\n/g, ' ').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, marginBottom: 4, lineHeight: 1.4, color: '#000', fontStyle: 'italic' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<span/i)) {
        // Handle span tags - extract text content (usually inline, so no extra margin)
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<strong/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, fontWeight: 'bold', color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<em/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, fontStyle: 'italic', color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<ul|<ol/i)) {
        // Extract list items
        const listItems = part.match(/<li[^>]*>(.*?)<\/li>/gi) || [];
        if (listItems.length > 0) {
          elements.push(
            <View key={index} style={{ marginLeft: 15, marginTop: 4, marginBottom: 8 }}>
              {listItems.map((item, liIndex) => {
                const text = item.replace(/<[^>]*>/g, '').trim();
                if (text) {
                  return (
                    <Text key={liIndex} style={{ fontSize: 10, marginBottom: 3, color: '#000' }}>
                      • {text}
                    </Text>
                  );
                }
                return null;
              })}
            </View>
          );
        }
      } else if (!part.match(/<[^>]*>/) && part.trim()) {
        // Plain text
        elements.push(
          <Text key={index} style={{ fontSize: 10, marginBottom: 4, color: '#000' }}>
            {part.trim()}
          </Text>
        );
      } else if (part.match(/<[^>]*>/)) {
        // Fallback: if there are HTML tags we don't recognize, strip them and render as plain text
        const text = part.replace(/<[^>]*>/g, '').replace(/\n/g, ' ').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, marginBottom: 4, color: '#000' }}>
              {text}
            </Text>
          );
        }
      }
    });
    
    return elements;
  };
  
  // Table renderer for HTML tables
  const renderTable = (tableHTML: string, tableIndex: number) => {
    // Extract table rows
    const rowMatches = tableHTML.match(/<tr[^>]*>[\s\S]*?<\/tr>/gi) || [];
    
    // Check if table has table-layout: fixed for better width handling
    const hasFixedLayout = tableHTML.match(/table-layout:\s*fixed/i);
    
    return (
      <View key={`table-${tableIndex}`} style={{ 
        marginTop: 10, 
        marginBottom: 10, 
        borderWidth: 1, 
        borderColor: '#000',
        width: '100%'
      }}>
        {rowMatches.map((rowHTML, rowIndex) => {
          // Extract cells (td or th)
          const cellMatches = rowHTML.match(/<t[hd][^>]*>[\s\S]*?<\/t[hd]>/gi) || [];
          
          return (
            <View key={`row-${rowIndex}`} style={{ 
              flexDirection: 'row',
              borderBottomWidth: rowIndex < rowMatches.length - 1 ? 1 : 0,
              borderBottomColor: '#ccc',
              minHeight: 25
            }}>
              {cellMatches.map((cellHTML, cellIndex) => {
                const cellText = cellHTML.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                
                // Check if this is a header cell or has bold styling
                const isHeader = cellHTML.match(/<th/i) || cellHTML.match(/font-weight:\s*bold/i);
                
                // Extract colspan if present
                const colspanMatch = cellHTML.match(/colspan\s*=\s*["']?(\d+)["']?/i);
                const colspan = colspanMatch ? parseInt(colspanMatch[1]) : 1;
                
                // Extract width if specified in style
                const widthMatch = cellHTML.match(/width:\s*([0-9.]+)%/i);
                let cellWidth;
                
                if (widthMatch) {
                  // Use the specified width percentage
                  cellWidth = `${parseFloat(widthMatch[1])}%`;
                } else if (hasFixedLayout) {
                  // For fixed layout tables, calculate width based on colspan
                  // Assume 6 columns total (from the medical table structure)
                  cellWidth = `${(100 / 6) * colspan}%`;
                } else {
                  // Default flex behavior
                  cellWidth = colspan > 1 ? `${(100 / 6) * colspan}%` : `${100 / 6}%`;
                }
                
                return (
                  <View key={`cell-${cellIndex}`} style={{ 
                    width: cellWidth,
                    padding: 3, // Reduced padding for better fit
                    borderRightWidth: cellIndex < cellMatches.length - 1 ? 1 : 0,
                    borderRightColor: '#ccc',
                    backgroundColor: isHeader ? '#f0f0f0' : 'transparent',
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <Text style={{ 
                      fontSize: 7, // Smaller font for better fit
                      fontWeight: isHeader ? 'bold' : 'normal',
                      color: '#000',
                      textAlign: 'center',
                      lineHeight: 1.2
                    }}>
                      {cellText || ''}
                    </Text>
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    );
  };
  
  return (
    <View style={{ marginTop: 5 }}>
      {renderHTML(notes)}
    </View>
  );
};

/**
 * Specialized renderer for survey records
 * Optimized for the HTML content found in surveyRecord.value
 */
export const PDFSurveyRecordRenderer: React.FC<{
  recordValue: string;
  fallbackStrategy?: FallbackStrategy;
}> = ({ recordValue, fallbackStrategy = 'plaintext' }) => {
  
  // Handle null/undefined recordValue
  if (!recordValue || typeof recordValue !== 'string') {
    return null;
  }
  
  // Simple HTML to PDF converter for common tags (same as clinical notes)
  const renderSimpleHTML = (htmlContent: string) => {
    const elements: React.ReactNode[] = [];
    
    // Split by common HTML tags and render accordingly
    const parts = htmlContent.split(/(<h[1-6][^>]*>.*?<\/h[1-6]>|<p[^>]*>.*?<\/p>|<strong[^>]*>.*?<\/strong>|<em[^>]*>.*?<\/em>|<ul[^>]*>.*?<\/ul>|<ol[^>]*>.*?<\/ol>|<li[^>]*>.*?<\/li>)/gi);
    
    parts.forEach((part, index) => {
      if (!part.trim()) return;
      
      if (part.match(/<h[1-6]/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 12, fontWeight: 'bold', marginTop: 10, marginBottom: 6, color: '#333' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<p/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, marginBottom: 6, lineHeight: 1.4, color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<strong/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, fontWeight: 'bold', color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<em/i)) {
        const text = part.replace(/<[^>]*>/g, '').trim();
        if (text) {
          elements.push(
            <Text key={index} style={{ fontSize: 10, fontStyle: 'italic', color: '#000' }}>
              {text}
            </Text>
          );
        }
      } else if (part.match(/<ul|<ol/i)) {
        // Extract list items
        const listItems = part.match(/<li[^>]*>(.*?)<\/li>/gi) || [];
        if (listItems.length > 0) {
          elements.push(
            <View key={index} style={{ marginLeft: 15, marginTop: 4, marginBottom: 8 }}>
              {listItems.map((item, liIndex) => {
                const text = item.replace(/<[^>]*>/g, '').trim();
                if (text) {
                  return (
                    <Text key={liIndex} style={{ fontSize: 10, marginBottom: 3, color: '#000' }}>
                      • {text}
                    </Text>
                  );
                }
                return null;
              })}
            </View>
          );
        }
      } else if (!part.match(/<[^>]*>/) && part.trim()) {
        // Plain text
        elements.push(
          <Text key={index} style={{ fontSize: 10, marginBottom: 4, color: '#000' }}>
            {part.trim()}
          </Text>
        );
      }
    });
    
    return elements;
  };
  
  return (
    <View style={{ marginTop: 5 }}>
      {renderSimpleHTML(recordValue)}
    </View>
  );
};

/**
 * Basic markdown renderer (simplified)
 * Handles: # headers, **bold**, *italic*, - lists
 */
const renderBasicMarkdown = (markdown: string): React.ReactElement[] => {
  const elements: React.ReactElement[] = [];
  const lines = markdown.split('\n');
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim();
    
    if (!trimmedLine) {
      elements.push(<Text key={`empty-${index}`} style={{ marginBottom: 4 }}>{'\n'}</Text>);
      return;
    }
    
    // Headers
    if (trimmedLine.startsWith('# ')) {
      elements.push(
        <Text key={`h1-${index}`} style={{ fontSize: 16, fontWeight: 'bold', marginBottom: 6 }}>
          {trimmedLine.substring(2)}
        </Text>
      );
    } else if (trimmedLine.startsWith('## ')) {
      elements.push(
        <Text key={`h2-${index}`} style={{ fontSize: 14, fontWeight: 'bold', marginBottom: 5 }}>
          {trimmedLine.substring(3)}
        </Text>
      );
    } else if (trimmedLine.startsWith('### ')) {
      elements.push(
        <Text key={`h3-${index}`} style={{ fontSize: 12, fontWeight: 'bold', marginBottom: 4 }}>
          {trimmedLine.substring(4)}
        </Text>
      );
    } 
    // Lists
    else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      elements.push(
        <Text key={`li-${index}`} style={{ fontSize: 10, marginBottom: 2, marginLeft: 10 }}>
          • {trimmedLine.substring(2)}
        </Text>
      );
    }
    // Regular paragraph
    else {
      // Handle basic inline formatting
      let processedText = trimmedLine;
      
      // Bold text **text**
      processedText = processedText.replace(/\*\*(.*?)\*\*/g, '$1');
      
      // Italic text *text*
      processedText = processedText.replace(/\*(.*?)\*/g, '$1');
      
      elements.push(
        <Text key={`p-${index}`} style={{ fontSize: 10, marginBottom: 4 }}>
          {processedText}
        </Text>
      );
    }
  });
  
  return elements;
};

/**
 * Utility function to detect content type
 */
export const detectContentType = (content: string): ContentType => {
  if (!content) return 'plaintext';
  
  // Check for HTML tags
  if (/<[^>]+>/.test(content)) {
    return 'html';
  }
  
  // Check for markdown patterns
  if (/^#+\s|^\*\*.*\*\*|\*.*\*|^-\s|^\*\s/.test(content)) {
    return 'markdown';
  }
  
  return 'plaintext';
};

/**
 * Utility function to clean content for PDF rendering
 */
export const cleanContentForPDF = (content: string): string => {
  if (!content) return '';
  
  return content
    .replace(/\r\n/g, '\n') // Normalize line endings
    .replace(/\r/g, '\n')   // Handle old Mac line endings
    .replace(/\t/g, '    ') // Convert tabs to spaces
    .trim();
};

